import {
  type CellContext,
  createColumnHelper,
  type PaginationState,
} from '@tanstack/react-table';
import { Loader2Icon, PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  type Instructor,
  useCreateInstructorMutation,
  useGetInstructors,
  useUpdateInstructorMutation,
} from '~entities/instructors';
import { useDeleteInstructorMutation } from '~entities/instructors/api/delete-instructor';

import { EnrollioTable } from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';
import { getStoredPageSize } from '~shared/helpers/page-size-preference';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

const columnHelper = createColumnHelper<Instructor>();

const columns = [
  columnHelper.accessor('fieldName', {
    header: (info) => (
      <DataTableColumnHeader
        disableHiding
        column={info.column}
        title='Instructor Name'
        className='pl-4 text-black'
      />
    ),
    cell: (info) => <div className='px-4 capitalize'>{info.getValue()}</div>,
  }),

  columnHelper.display({
    id: 'Action',
    header: (info) => (
      <DataTableColumnHeader
        disableHiding
        column={info.column}
        title='Action'
        className='justify-end px-4 text-black'
      />
    ),
    cell: (info) => {
      return <InstructorActionCell info={info} />;
    },
  }),
];

export function InstructorListPage() {
  const [query, setQuery] = useState<string>('');

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: getStoredPageSize(),
  });

  const { data, isFetching } = useGetInstructors({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    ...(query ? { search: query } : {}),
  });

  const tableData = data?.items ?? [];

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newInstructorName, setNewInstructorName] = useState('');
  const createMutation = useCreateInstructorMutation();

  const handleCreateInstructor = async () => {
    try {
      await createMutation.mutateAsync({
        fieldName: newInstructorName,
      });
      setIsAddDialogOpen(false);
      setNewInstructorName('');
      toast.success('Instructor created successfully');
    } catch (error) {
      toast.error('Failed to create instructor');
    }
  };

  return (
    <div className='w-full min-w-0 md:w-1/2 lg:w-1/3'>
      <EnrollioTable
        ariaLabel='Instructors'
        totalLabel='Instructors'
        data={tableData}
        columns={columns}
        rowCount={data?.pagination.total}
        isUpdating={isFetching}
        manualPagination
        pagination={pagination}
        onPaginationChange={setPagination}
        filterHeader={() => {
          return (
            <div className='flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
              <Input
                placeholder='Search Instructors'
                value={query}
                className='w-full sm:w-3/12'
                onChange={(event) => {
                  setQuery(event.target.value);
                }}
              />
              <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant='secondary'
                    size='sm'
                    className='w-full sm:w-auto'
                  >
                    <PlusIcon />
                    Add New
                  </Button>
                </DialogTrigger>
                <DialogContent className='sm:max-w-[425px]'>
                  <DialogHeader>
                    <DialogTitle>Add New Instructor</DialogTitle>
                    <DialogDescription>
                      Create a new instructor by entering the name below.
                    </DialogDescription>
                  </DialogHeader>
                  <div className='grid gap-4 py-4'>
                    <div className='grid grid-cols-4 items-center gap-4'>
                      <Label htmlFor='name' className='text-right'>
                        Instructor Name
                      </Label>
                      <Input
                        id='name'
                        value={newInstructorName}
                        onChange={(event) => {
                          setNewInstructorName(event.target.value);
                        }}
                        className='col-span-3'
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      variant={'outline'}
                      onClick={() => {
                        setIsAddDialogOpen(false);
                        setNewInstructorName('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type='submit'
                      onClick={handleCreateInstructor}
                      disabled={
                        createMutation.isPending || !newInstructorName.trim()
                      }
                    >
                      {createMutation.isPending ? (
                        <Loader2Icon className='animate-spin' />
                      ) : (
                        'Create'
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          );
        }}
      />
    </div>
  );
}

function InstructorActionCell({
  info,
}: {
  info: CellContext<Instructor, unknown>;
}) {
  const mutation = useUpdateInstructorMutation();
  const deleteMutation = useDeleteInstructorMutation();
  const initialInstructorName = info.row.original.fieldName;
  const instructorId = info.row.original._id;

  const [instructorName, setInstructorName] = useState(initialInstructorName);
  const [isOpen, setIsOpen] = useState(false);

  const handleSave = async () => {
    try {
      await mutation.mutateAsync({
        fieldId: instructorId,
        fieldName: instructorName,
      });
      setIsOpen(false);
      toast.success('Instructor updated successfully');
    } catch (error) {
      console.error(error);
      toast.error('Failed to update instructor');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync({
        fieldId: instructorId,
      });
      toast.success('Instructor deleted successfully');
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete instructor');
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <div className='flex items-center justify-end gap-2 px-4'>
          <TooltipProvider>
            <Tooltip>
              <DialogTrigger asChild>
                <TooltipTrigger asChild>
                  <Button className='size-6 p-0 text-secondary [&_svg]:size-3'>
                    <PencilIcon />
                  </Button>
                </TooltipTrigger>
              </DialogTrigger>
              <TooltipContent>
                <p className='text-secondary'>Edit Instructor</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={'destructive'}
                  className='size-6 p-0 [&_svg]:size-3'
                  onClick={handleDelete}
                >
                  <Trash2Icon />
                </Button>
              </TooltipTrigger>
              <TooltipContent className='bg-destructive'>
                <p className='text-destructive-foreground'>Delete Instructor</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <DialogContent className='sm:max-w-[425px]'>
          <DialogHeader>
            <DialogTitle>Edit Instructor</DialogTitle>
            <DialogDescription>
              Make changes to your Instructor here. Click save when you&apos;re
              done.
            </DialogDescription>
          </DialogHeader>
          <div className='grid gap-4 py-4'>
            <div className='grid grid-cols-4 items-center gap-4'>
              <Label htmlFor='name' className='text-right'>
                Instructor Name
              </Label>
              <Input
                id='name'
                value={instructorName}
                onChange={(event) => {
                  setInstructorName(event.target.value);
                }}
                className='col-span-3'
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant={'outline'}
              onClick={() => {
                setIsOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type='submit'
              onClick={handleSave}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? (
                <Loader2Icon className='animate-spin' />
              ) : (
                'Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
